import * as _ from "lodash-es";
import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import "./RMA.scss";
import { Link, useNavigate } from "react-router-dom";
import IOwTableCols from "@models/IOwTableCols";
import IRMA from "@models/IRMA";
import UIService from "@services/UIService";
import OwButton from "@components/shared/OwButton/OwButton";
import OwInputWrapper from "@components/shared/OwInputWrapper/OwInputWrapper";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import OwTable from "@components/shared/OwTable/OwTable";
import OwTitle from "@components/shared/OwTitle/OwTitle";
import PaginationBar from "@components/shared/PaginationBar/PaginationBar";
import INotification from "@models/INotification";
import NotificationPositionEnum from "@models/NotificationPositionEnum";
import NotificationLevelEnum from "@models/NotificationLevelEnum";
import OwActionBar from "@components/shared/OwActionBar/OwActionBar";
import UserContext from "@contexts/UserContext";
import SupplierContext from "@contexts/SupplierContext";
import UiContext from "@contexts/UiContext";
import { OwSelectOptionFn } from "@components/shared/OwSelectOptionFn/OwSelectOptionFn";
import SortOrderEnum from "@models/SortOrderEnum";
import { ConfirmDeleteOverlayFn } from "@components/shared/ConfirmDeleteOverlayFn/ConfirmDeleteOverlayFn";
import { SearchSection } from "@components/shared/SearchSection/SearchSection";
import { useRmaCols } from "@hooks/rma/useRmaCols";
import RmaStatusEnum from "@models/RmaStatusEnum";
import { buildAuthHeadersForFetch, buildRestUrlWithQuery } from "@services/RestService";
import RestEndpointsEnum from "@models/RestEndpointsEnum";
import IAction from "@models/IAction";

type Props = {};

export const RMA = (props: Props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const uiContext = useContext(UiContext);
    const navigate = useNavigate();

    const rmaStatusOptions: { status: RmaStatusEnum; title: string }[] = [
        { status: RmaStatusEnum.DRAFT, title: "draft" },
        { status: RmaStatusEnum.AUTHORIZED, title: "authorized" },
        { status: RmaStatusEnum.RECEIVED, title: "received" },
        { status: RmaStatusEnum.PROCESSED, title: "processed" },
        { status: RmaStatusEnum.CANCELLED, title: "cancelled" },
    ];

    const [rmas, setRmas] = React.useState<IRMA[]>([]);
    const [totalRmas, setTotalRmas] = React.useState<number>(0);
    const [columns, setColumns] = React.useState<IOwTableCols[]>([]);
    const [offset, setOffset] = React.useState<number>(0);
    const [limit, setLimit] = React.useState<number>(25);
    const [isDataFetching, setIsDataFetching] = React.useState<boolean>(false);
    const [isInitialData, setIsInitialData] = React.useState<boolean>(true);

    // Multi-select state
    const [selectedRmaIds, setSelectedRmaIds] = React.useState<Set<number>>(new Set());
    const [selectedRmas, setSelectedRmas] = React.useState<IRMA[]>([]);
    const [hasOneChecked, setHasOneChecked] = React.useState<boolean>(false);

    // Search filters
    const [rmaNumber, setRmaNumber] = React.useState<string>("");
    const [status, setStatus] = useState<RmaStatusEnum | undefined | null>();
    const [orderNumber, setOrderNumber] = React.useState<string>("");
    const [confirmDeleteRow, setConfirmDeleteRow] = React.useState<string | null>(null);
    const [selectedRma, setSelectedRma] = React.useState<IRMA | null>(null);
    const [resetSearch, setResetSearch] = React.useState<boolean>(false);

    // Sorting
    const [orderBy, setOrderBy] = useState<string>("updated_at");
    const [orderDir, setOrderDir] = useState<SortOrderEnum>(SortOrderEnum.DESC);

    const toggleConfirmDelete = (row: IRMA, index: number) => {
        setConfirmDeleteRow(`row-${index}`);
        setSelectedRma(row);
    };

    const handleSelectRma = (rma: IRMA) => {
        const newSelectedIds = new Set(selectedRmaIds);

        if (newSelectedIds.has(rma.id)) {
            newSelectedIds.delete(rma.id);
            setSelectedRmas((prev) => prev.filter((r) => r.id !== rma.id));
        } else {
            newSelectedIds.add(rma.id);
            setSelectedRmas((prev) => [...prev, rma]);
        }

        setSelectedRmaIds(newSelectedIds);
        setHasOneChecked(newSelectedIds.size > 0);
    };

    const { getColumns, getActions } = useRmaCols({
        toggleConfirmDelete,
        rmas,
        orderBy,
        orderDir,
        setOrderBy,
        setOrderDir,
        selectedIds: selectedRmaIds,
        onSelectRow: handleSelectRma,
    });

    const [actions, setActions] = React.useState<IAction[]>(getActions());

    useEffect(() => {
        document.title = `Orderwerks >> RMA`;
    }, []);

    useEffect(() => {
        if (resetSearch) {
            setResetSearch(false);
            search();
        }
    }, [resetSearch]);

    useEffect(() => {
        setColumns(getColumns());
        setActions(getActions());
    }, [isDataFetching, orderDir, orderBy, hasOneChecked, selectedRmaIds]);

    useEffect(() => {
        if (!isDataFetching) {
            search();
        }
    }, [orderDir, orderBy]);

    useEffect(() => {
        setColumns(getColumns());
    }, [rmas]);

    useEffect(() => {
        if (!isInitialData) {
            search();
        }
    }, [limit, offset]);

    const getRmas = async (queryString?: string) => {
        setIsDataFetching(true);

        try {
            const query: Record<string, any> = {
                limit,
                offset,
            };

            if (!_.isNil(rmaNumber) && rmaNumber.length) {
                query.rma_number = rmaNumber;
            }

            if (!_.isNil(status)) {
                query.rma_status_id = status;
            }

            if (!_.isNil(orderNumber) && orderNumber.length) {
                query.order_name = orderNumber;
            }

            query.order_by = orderBy;
            query.order_dir = orderDir;

            const url = buildRestUrlWithQuery({
                endpoint: RestEndpointsEnum.GetRmas,
                query,
            });

            const headers = buildAuthHeadersForFetch({
                userModel,
                supplierModel,
            });

            const response = await fetch(url, {
                method: "GET",
                headers,
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch RMAs: ${response.statusText}`);
            }

            const data = await response.json();

            setRmas(data.rmas || []);
            setTotalRmas(data.totalCount || 0);
            setIsInitialData(false);

            // Clear selections when new data is loaded
            setSelectedRmaIds(new Set());
            setSelectedRmas([]);
            setSelectedRma(null);
            setHasOneChecked(false);
        } catch (error) {
            console.error("Error fetching RMAs:", error);
            const notification: INotification = {
                message: "error_fetching_rmas",
                position: NotificationPositionEnum.bc,
                level: NotificationLevelEnum.error,
            };
            uiContext.notifyFn(null, notification);
            setRmas([]);
            setTotalRmas(0);
        } finally {
            setIsDataFetching(false);
        }
    };

    const paginationChanged = (_offset: number, _limit: number) => {
        setOffset(_offset);
        setLimit(_limit);
    };

    const handleChange = (e: ChangeEvent<any>) => {
        const value = e.target.type === "checked" ? e.target.checked : e.target.value;
        const _name = e.target.name;

        switch (_name) {
            case "rma_number":
                setRmaNumber(value);
                break;
            case "rma_status_id":
                setStatus(Number(value) as RmaStatusEnum);
                break;
            case "order_number":
                setOrderNumber(value);
                break;
            default:
                break;
        }
    };

    const search = () => {
        getRmas();
    };

    const reset = () => {
        setOffset(0);
        setLimit(25);
        setRmaNumber("");
        setOrderNumber("");
        setStatus(null);
        setOrderBy("updated_at");
        setOrderDir(SortOrderEnum.DESC);
        setResetSearch(true);
    };

    const enterSearch = (e: any) => {
        if (e.key === "Enter" || e.keyCode === 13) {
            search();
        }
    };

    const cancelDelete = () => {
        setConfirmDeleteRow(null);
    };

    const commitDelete = async () => {
        if (selectedRma) {
            try {
                const url = buildRestUrlWithQuery({
                    endpoint: RestEndpointsEnum.DeleteRma,
                    params: { rmaGuid: selectedRma.guid },
                });

                const headers = buildAuthHeadersForFetch({
                    userModel,
                    supplierModel,
                });

                const response = await fetch(url, {
                    method: "DELETE",
                    headers,
                });

                if (!response.ok) {
                    throw new Error(`Failed to delete RMA: ${response.statusText}`);
                }

                const notification: INotification = {
                    message: "successfully_deleted_rma",
                    position: NotificationPositionEnum.bc,
                    level: NotificationLevelEnum.success,
                };

                uiContext.notifyFn(null, notification);

                setSelectedRma(null);
                setSelectedRmaIds(new Set());
                setSelectedRmas([]);
                setConfirmDeleteRow(null);
                setHasOneChecked(false);

                search();
            } catch (error) {
                console.error("Error deleting RMA:", error);
                const notification: INotification = {
                    message: "error_deleting_rma",
                    position: NotificationPositionEnum.bc,
                    level: NotificationLevelEnum.error,
                };
                uiContext.notifyFn(null, notification);
            }
        }
    };

    let cardClass = "dark-card text-white bg-secondary";

    if (UIService.isMainViewLight()) {
        cardClass = "";
    }

    const checkAll = (isChecked: boolean) => {
        if (isChecked) {
            const allIds = new Set(rmas.map((rma) => rma.id));
            setSelectedRmaIds(allIds);
            setSelectedRmas([...rmas]);
            setHasOneChecked(rmas.length > 0);
        } else {
            setSelectedRmaIds(new Set());
            setSelectedRmas([]);
            setSelectedRma(null);
            setHasOneChecked(false);
        }
    };

    return (
        <div className="RMA position-relative">
            <div className="row mt-2">
                <div className={"col-12 align-items-center"}>
                    <div className={"d-flex w-100 align-items-center"}>
                        <OwTitle leftIconName={"fas fa-undo"} title={"rma"} />
                    </div>
                </div>
            </div>

            <div className="row mt-2">
                <div className={"col-12"}>
                    <SearchSection>
                        <div className={"row"}>
                            <div className={"col-12 col-md-4"}>
                                <OwLabel title={"rma_number"} />
                                <OwInputWrapper
                                    placeholder={"rma_number"}
                                    fn={handleChange}
                                    type={"text"}
                                    onKeyDownFn={enterSearch}
                                    name={"rma_number"}
                                    value={rmaNumber}
                                />
                            </div>

                            <div className={"col-12 col-md-4"}>
                                <OwLabel title={"order"} />
                                <OwInputWrapper
                                    placeholder={"order"}
                                    fn={handleChange}
                                    type={"text"}
                                    onKeyDownFn={enterSearch}
                                    name={"order_number"}
                                    value={orderNumber}
                                />
                            </div>

                            <div className={"col-12 col-md-4"}>
                                <OwLabel title={"status"} />
                                <OwSelectOptionFn
                                    fn={handleChange}
                                    titleProp={"title"}
                                    valueProp={"status"}
                                    name={"rma_status_id"}
                                    options={rmaStatusOptions}
                                />
                            </div>
                        </div>

                        <div className={"row mt-3"}>
                            <div className={"col-12"}>
                                <OwButton title={"search"} fn={search} className={"btn-primary me-2"} />
                                <OwButton title={"reset"} fn={reset} className={"btn-secondary"} />
                            </div>
                        </div>
                    </SearchSection>
                </div>
            </div>

            {hasOneChecked && actions.length ? (
                <div className="row mt-2">
                    <div className={"col-12"}>
                        <OwActionBar actions={actions} columns={{}} colToggledFn={() => {}} toggleSmallTable={() => {}} />
                    </div>
                </div>
            ) : null}

            <div className="row mt-2">
                <div className={"col-12"}>
                    <OwTable
                        columns={columns}
                        dataSet={rmas}
                        userModel={userModel}
                        supplierModel={supplierModel}
                        dataFetch={isDataFetching}
                        initialData={isInitialData}
                        checkAllFn={checkAll}
                    />
                </div>
            </div>

            <div className="row mt-2">
                <div className={"col-12"}>
                    <PaginationBar offset={offset} limit={limit} totalRecords={totalRmas} onPaginationChanged={paginationChanged} />
                </div>
            </div>

            <ConfirmDeleteOverlayFn
                confirmDeleteRow={confirmDeleteRow}
                commitDeleteFn={commitDelete}
                cancelDeleteFn={cancelDelete}
                tableId={"rmaTable"}
            />
        </div>
    );
};
